import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, TextField } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Loading, Tabs } from '../components/common';
import { useHubLogin } from '../session';
import {
  defaultTarget,
  loadTargets,
  setActiveTarget,
  signInTarget,
  targetLoginError,
  useLogin,
  useTargets,
  type AuthTargetInfo,
} from './session';
import { READ_SCOPE } from './api';
import { startTargetSso } from '../sso';

/** Sign in to one AUTH environment with the operator's own credentials (the hub never stores the password). */
export function TargetSignIn({ target }: { target: AuthTargetInfo }) {
  const hub = useHubLogin();
  const [username, setUsername] = useState(hub?.username ?? '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const federated = async () => {
    setRedirecting(true);
    setError(null);
    try {
      await startTargetSso(target);
    } catch (err) {
      setRedirecting(false);
      setError((err as Error).message);
    }
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signInTarget(target.id, username.trim(), password);
      setPassword('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="nyk-card" data-testid="target-signin">
      <div className="nyk-card-head">
        <h2>Not signed in to {target.label}</h2>
      </div>
      <p className="nyk-card-intro">
        Sign-in to {target.label} ({target.authUrl}) failed or has ended. Sign in with your NYCE operator
        account to administer it; the password is only passed on to that AUTH.
      </p>
      <ErrorBox
        error={error ?? (targetLoginError(target.id) && `Last sign-in: ${targetLoginError(target.id)}`)}
      />
      {target.sso && (
        <div className="oph-target-sso">
          <Button variant="secondary" loading={redirecting} onClick={() => void federated()}>
            Sign in with Microsoft
          </Button>
          <span className="nyk-ui-hint">or with your password:</span>
        </div>
      )}
      <form className="oph-target-signin" onSubmit={(e) => void submit(e)}>
        <TextField
          label="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
        />
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
        />
        <Button type="submit" loading={busy} disabled={!username.trim() || !password}>
          Sign in
        </Button>
      </form>
    </div>
  );
}

/**
 * Frame of every identity page: the AUTH environment picker (`?target=`), its sign-in state, and the page itself
 * once that environment is signed in. `scim` / `buddy` / `design` / `printqueue` / `automation` pages list only environments with a
 * SCIM gateway / a nyce.buddy / a NYCE Design / a print queue / an automation gateway (and need no AUTH admin scope there: those services
 * check their own; the print queue and the automation gateway need no AUTH session at all). The page remounts on
 * a switch, so nothing of one environment leaks into the other.
 */
export type TargetService = 'auth' | 'scim' | 'buddy' | 'design' | 'printqueue' | 'automation';

const NONE: Record<TargetService, { title: string; hint: string }> = {
  auth: {
    title: 'No AUTH environments configured',
    hint: "The hub's AuthTargets setting lists the environments it administers.",
  },
  scim: { title: 'No SCIM gateways configured', hint: 'Set ScimUrl on an AuthTargets entry.' },
  buddy: { title: 'No nyce.buddy configured', hint: 'Set BuddyUrl on an AuthTargets entry.' },
  design: { title: 'No NYCE Design configured', hint: 'Set DesignUrl on an AuthTargets entry.' },
  printqueue: {
    title: 'No print queue configured',
    hint: 'Set PrintQueueUrl on an AuthTargets entry and the hub’s PrintQueue:Tokens:<id>.',
  },
  automation: {
    title: 'No automation gateway configured',
    hint: 'Set AutomationGwUrl on an AuthTargets entry and the hub’s AutomationGw:Tokens:<id>.',
  },
};

export function TargetShell({
  children,
  service = 'auth',
}: {
  children: ReactNode;
  service?: TargetService;
}) {
  const [params, setParams] = useSearchParams();
  const [loaded, setLoaded] = useState(false);
  const { targets, active, signedIn } = useTargets();
  const login = useLogin();
  const list =
    service === 'scim'
      ? targets.filter((t) => !!t.scimUrl)
      : service === 'buddy'
        ? targets.filter((t) => !!t.buddyUrl)
        : service === 'design'
          ? targets.filter((t) => !!t.designUrl)
          : service === 'printqueue'
            ? targets.filter((t) => !!t.printQueue)
            : service === 'automation'
              ? targets.filter((t) => !!t.automationGw)
              : targets;
  // The print queue and the automation gateway are called with the hub's own key: no AUTH session needed.
  const needsSession = service !== 'printqueue' && service !== 'automation';
  const want = params.get('target') ?? '';

  useEffect(() => {
    void loadTargets().then(() => setLoaded(true));
  }, []);
  useEffect(() => {
    if (!loaded) return;
    const id = list.some((t) => t.id === want)
      ? want
      : list.some((t) => t.id === defaultTarget())
        ? defaultTarget()
        : (list[0]?.id ?? '');
    setActiveTarget(id);
    if (id && id !== want)
      setParams(
        (cur) => {
          const next = new URLSearchParams(cur);
          next.set('target', id);
          return next;
        },
        { replace: true }
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, want, list.length]);

  if (!loaded) return <Loading loading />;
  if (!list.length) return <EmptyState card title={NONE[service].title} hint={NONE[service].hint} />;
  const target = list.find((t) => t.id === active);
  if (!target) return <Loading loading />;
  const select = (id: string) =>
    setParams((cur) => {
      const next = new URLSearchParams();
      // other filters belong to the previous environment's data
      next.set('target', id);
      if (cur.get('tab')) next.set('tab', cur.get('tab')!);
      return next;
    });
  return (
    <>
      {list.length > 1 && (
        <div className="oph-target-bar" data-testid="target-bar">
          <span className="nyk-ui-hint">Environment</span>
          <Tabs
            value={active}
            onChange={select}
            options={list.map((t) => ({
              value: t.id,
              label: !needsSession || signedIn(t.id) ? t.label : `${t.label} · signed out`,
            }))}
          />
        </div>
      )}
      {target && needsSession && !signedIn(target.id) ? (
        <TargetSignIn key={target.id} target={target} />
      ) : service === 'auth' && login && !login.access.canRead ? (
        <EmptyState
          card
          title={`No AUTH admin access on ${target?.label}`}
          hint={`Your token there lacks ${READ_SCOPE}, or your hub account lacks ophub:auth:read.`}
        />
      ) : (
        <div key={active}>{children}</div>
      )}
    </>
  );
}
